import { useState } from 'react';
import type { MutableRefObject } from 'react';
import { useChat, ChatType } from '../../contexts/ChatContext';
import { smsService, SendSMSRequest } from '../../services/smsService';
import { whatsappService, SendWhatsAppRequest } from '../../services/whatsappService';
import messageService from '../../services/messageService';
import { useUserStore } from '../../stores/userStore';
import { showError } from '../../utils/notifications';

interface PeerSenderOptions {
  chatType: ChatType;
  tenantId?: string;
  tenantName?: string;
  tenantPhone?: string;
  createdConversationsRef: MutableRefObject<Set<string>>;
}

/**
 * Sending for the tenant, SMS and WhatsApp threads. These handlers are the
 * old UnifiedChat ones moved here as-is: same service calls, same system
 * messages. The only change is that they now receive the text from the
 * composer instead of reading a shared input state.
 */
export function usePeerSenders({ chatType, tenantId, tenantName, tenantPhone, createdConversationsRef }: PeerSenderOptions) {
  const { addMessageLegacy, addTenantConversation } = useChat();
  const [isSending, setIsSending] = useState(false);

  // Handle sending a message (for tenant chat)
  const handleTenantMessage = async (content: string) => {
    if (!content.trim() || isSending) return;
    
    const messageContent = content.trim();
    setIsSending(true);
    
    try {
      // Ensure tenant conversation exists in sidebar
      if (tenantId && tenantName) {
        const conversationKey = `tenant:${tenantId}`;
        if (!createdConversationsRef.current.has(conversationKey)) {
          addTenantConversation(tenantId, tenantName);
          createdConversationsRef.current.add(conversationKey);
        }
      }
      
      // Add message to chat
      addMessageLegacy({
        role: 'user',
        content: messageContent,
      }, chatType);
      
      // Note: message is sent directly below via messageService.sendDirectMessage
      // (using the resolved actual User ID), so we intentionally do NOT also call
      // persistMessage here — persistMessage would send a duplicate message to the
      // tenant's contact ID, which may not resolve to a real user.

      // Send message to backend
      if (tenantId) {
        // Get the User ID from the Contact ID
        const contactId = parseInt(tenantId);
        const { users } = useUserStore.getState();
        let user = users.find((u: any) => u.contact_id === contactId);
        
        // If user not found, try to fetch users
        if (!user) {
          const { fetchUsers } = useUserStore.getState();
          await fetchUsers();
          const updatedState = useUserStore.getState();
          user = updatedState.users.find((u: any) => u.contact_id === contactId);
        }
        
        if (!user) {
          throw new Error(`No user found for contact ID ${contactId}`);
        }
        await messageService.sendDirectMessage({
          receiver_id: user.id, // Use the actual User ID
          content: messageContent,
          message_type: 'text'
        });
        
        // Add confirmation message
        addMessageLegacy({
          role: 'system',
          content: `Message sent to ${tenantName}`,
        }, chatType);
      }
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      // Add error message to chat
      addMessageLegacy({
        role: 'system',
        content: `Failed to send message: ${errorMessage}`,
      }, chatType);
      showError(`Failed to send message: ${errorMessage}`);
    } finally {
      setIsSending(false);
    }
  };

  // Handle SMS messages
  const handleSMSMessage = async (messageContent: string) => {
    if (!messageContent.trim() || !tenantPhone) return;

    setIsSending(true);
    try {
      // Add user message immediately
      addMessageLegacy({
        role: 'user',
        content: `SMS to ${tenantPhone}: ${messageContent}`,
      }, 'sms');

      // Send SMS
      const smsRequest: SendSMSRequest = {
        to: tenantPhone,
        message: messageContent,
        template_id: '',
        variables: {}
      };

      const smsResponse = await smsService.sendSMS(smsRequest);

      // Add success message
      addMessageLegacy({
        role: 'system',
        content: `SMS sent successfully. Message ID: ${smsResponse.id}`,
      }, 'sms');

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      addMessageLegacy({
        role: 'system',
        content: `Failed to send SMS: ${errorMessage}`,
      }, 'sms');
      showError(`Failed to send SMS: ${errorMessage}`);
    } finally {
      setIsSending(false);
    }
  };

  // Handle WhatsApp messages
  const handleWhatsAppMessage = async (messageContent: string) => {
    if (!messageContent.trim() || !tenantPhone) return;

    setIsSending(true);
    try {
      // Format phone number to E.164 format
      const formattedPhone = whatsappService.formatPhoneNumber(tenantPhone);

      // Add user message immediately
      addMessageLegacy({
        role: 'user',
        content: `WhatsApp to ${formattedPhone}: ${messageContent}`,
      }, 'whatsapp');

      // Send WhatsApp
      const whatsappRequest: SendWhatsAppRequest = {
        to: formattedPhone,
        message: messageContent,
      };

      const whatsappResponse = await whatsappService.sendMessage(whatsappRequest);

      // Add success message
      addMessageLegacy({
        role: 'system',
        content: `WhatsApp sent successfully${whatsappResponse.message_id ? `. Message ID: ${whatsappResponse.message_id}` : ''}`,
      }, 'whatsapp');

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      addMessageLegacy({
        role: 'system',
        content: `Failed to send WhatsApp: ${errorMessage}`,
      }, 'whatsapp');
      showError(`Failed to send WhatsApp: ${errorMessage}`);
    } finally {
      setIsSending(false);
    }
  };

  return { isSending, handleTenantMessage, handleSMSMessage, handleWhatsAppMessage };
}
